import { useRef, useState, type DragEvent } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

type UploadCardProps = {
  busy: boolean;
  onFile: (file: File) => void;
};

export function UploadCard({ busy, onFile }: UploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file && !busy) {
      onFile(file);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>2. Upload the file</CardTitle>
        <CardDescription>
          The file is checked against the stored values; nothing is written
          until you apply it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          data-testid="bulk-import-dropzone"
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            'tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-lg tw:border-2 tw:border-dashed tw:p-8 tw:text-center',
            dragging && 'tw:border-primary tw:bg-muted/50',
          )}
        >
          {busy ? (
            <Spinner className="tw:size-6" />
          ) : (
            <Upload className="tw:size-6" />
          )}
          <p className="tw:text-sm">
            {busy ? 'Validating…' : 'Drop a .csv file here, or'}
          </p>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            Choose file
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="tw:hidden"
            data-testid="bulk-import-file-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) {
                onFile(file);
              }
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
